import { Box } from '@mui/material';

import { axisFieldAttr, useAxisHoverMark } from './axisHover';
import { AxisLabel } from './bookingsAxis';

export interface AxisBoundaryHeaderProps {
  left?: AxisLabel;
  /**
   * This header's own column field (`__period_<n>`), naming what its date emphasizes below it —
   * the anchor gridline for an ordinary boundary, and for a break (AxisGapHeader delegates its
   * date here) the compressed stretch's own band, which stands in for the gridline that column
   * cannot draw. See AXIS_BREAK_BAND_CLASS.
   */
  field: string;
  /**
   * The second column a MERGED label speaks for (`AxisLabel.pairedIdx`, resolved to its field by
   * the hook). A merged label lives in the FIRST anchor's column while captioning two boundaries:
   * with `left.pairPerLine` each date line hovers to its own column's gridline; a dash form
   * (one text naming both dates) marks both columns at once. Absent on a solo label.
   */
  pairedField?: string;
  /**
   * Pixels from THIS header cell's own left edge to center the label on — the boundary line's
   * position in the cell's own local coordinate space, i.e. `left.centerX - segment.x0` (the
   * hook computes this; this component never sees rail-global coordinates, only its own cell).
   * Zero for the overwhelming common case: an anchor's boundary line IS its own column's left
   * edge by construction, so the label centers exactly at the cell's own origin, overhanging both
   * directions like a chart axis. Only nonzero when the resolver's origin clamp
   * (bookingsAxis.ts's centerFor) shifted a label right to keep it from bleeding past the rail's
   * own start.
   */
  labelOffsetX?: number;
}

export const AXIS_LABEL_COLOR = '#615E6F';

// Mirrors GeneratikTable.styles.tsx's '.MuiDataGrid-columnHeaderTitle' rule, so a bookings-axis
// header reads exactly like every other timeline header instead of its own smaller type. Every
// axis header text shares this one style now — date labels and AxisGapHeader's own caption alike
// — there is no more anchor/tick emphasis split.
export const AXIS_LABEL_SX = {
  fontFamily: 'Neue Frutiger World',
  fontSize: 'var(--gt-font-size, 0.875rem)',
  fontWeight: 500,
  lineHeight: 1.5,
  color: AXIS_LABEL_COLOR,
  whiteSpace: 'nowrap' as const,
};

// zIndex: 1 lets an overhanging label paint above the NEXT header cell's own opaque background.
// MUI's DataGrid header cells (.MuiDataGrid-columnHeader, non-pinned) are `position: relative`
// with no z-index of their own, so they never form a stacking context — every header cell's
// content paints together, in DOM order, at the shared ancestor's "auto" layer. Without an
// explicit z-index, a label overhanging its column into the next one is drawn UNDER that next
// cell's background, since it comes later in the DOM — visually identical to being clipped. A
// positive z-index here lifts the label into that shared ancestor's positive layer instead,
// above every sibling header's auto-layer content, regardless of DOM order.
//
// `left` is a pixel offset (labelOffsetX), not a percentage: this positions the label relative to
// the OUTER wrapper Box below (`position: absolute; inset: 0`), not the '50%'-of-column-width it
// used to be. That wrapper's own containing block is the header CELL itself — '.timeline-axis-
// header' in GeneratikTable.styles.tsx sets `position: relative` on the cell and forces
// `.MuiDataGrid-columnHeaderTitleContainer`/`Content` (MUI's own, separately padded wrapper) to
// `position: static`/`overflow: visible`, taking them out of the containing-block chain entirely
// — confirmed empirically (getBoundingClientRect of the tick below and of the cell itself agree
// to the pixel). `left: 0` therefore already lands exactly at the cell's true edge, which is the
// same reference frame the edge tick has always used — no new CSS needed for this to hold.
//
// display: flex + flexDirection: column stacks a two-line label's lines vertically, each
// horizontally centered by alignItems — and is a no-op for a one-line label (a single text node
// is still its container's only flex item, laid out exactly as it was before this was added).
// lineHeight is tightened from AXIS_LABEL_SX's shared 1.5 to fit two lines inside the header:
// two 0.875rem (14px, the default 16px root * 0.875, confirmed no root font-size override exists
// in this app) lines at 1.2 is 2 * 14 * 1.2 = 33.6px, comfortably inside the 45px
// --gt-header-height default with ~11px to spare — while a single line at 1.2 vs. 1.5 is visually
// indistinguishable once centered, so it's applied unconditionally rather than only when stacked.
// pointerEvents: 'auto' re-enables hover on the label alone — '.timeline-period-header' turns
// pointer events off for the whole header cell, and pointer-events inherits, so without this the
// date could never be hovered. The cursor stays 'default' rather than the text I-beam the
// re-enabled events would otherwise summon: the date is an axis mark, not selectable text.
const LABEL_SX = {
  position: 'absolute' as const,
  top: '50%',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  pointerEvents: 'auto' as const,
  cursor: 'default',
  ...AXIS_LABEL_SX,
  lineHeight: 1.2,
};

// Round 19 ("can we remove the vertical line only from timeline header?"): the header carries
// only the date text now — no edge tick. An anchor boundary's vertical line still exists, but it
// begins at the first data row: the full-height row gridline is the timeline-axis-cell--anchor
// ::before machinery (GeneratikTable.styles.tsx), fully independent of this component.
const AxisBoundaryHeader = ({
  left,
  field,
  pairedField,
  labelOffsetX = 0,
}: AxisBoundaryHeaderProps) => {
  const { markHover, clearHoverMark, hoverRef } = useAxisHoverMark();

  // Cross-month merged form: line1 and line2 are the pair's two dates, one per line, so each
  // line targets its own boundary's gridline. Every other shape hovers as one unit — a solo
  // label marks its one column, a dash-merged label (whose single text names both dates) marks
  // both at once, space-separated (the styles' ~= matching lights each word's column).
  //
  // The same split decides which element carries AXIS_FIELD_ATTR, i.e. what lights up when the
  // mark is raised from the GRIDLINE end instead: one unit means the whole label, per-line means
  // each line answers only for its own boundary.
  const perLine = pairedField != null && !!left?.pairPerLine;
  const wholeLabelMark = pairedField != null ? `${field} ${pairedField}` : field;

  return (
    <Box sx={{ position: 'absolute', inset: 0 }}>
      {left != null && (
        <Box
          ref={hoverRef}
          data-testid="axis-boundary-label"
          {...axisFieldAttr(perLine ? undefined : wholeLabelMark)}
          onMouseEnter={perLine ? undefined : markHover(wholeLabelMark)}
          onMouseLeave={perLine ? undefined : clearHoverMark}
          sx={{ ...LABEL_SX, left: `${labelOffsetX}px`, transform: 'translate(-50%, -50%)' }}
        >
          {left.line2 != null ? (
            <>
              <Box
                component="span"
                data-testid="axis-boundary-label-line"
                {...axisFieldAttr(perLine ? field : undefined)}
                onMouseEnter={perLine ? markHover(field) : undefined}
                onMouseLeave={perLine ? clearHoverMark : undefined}
              >
                {left.line1}
              </Box>
              <Box
                component="span"
                data-testid="axis-boundary-label-line"
                {...axisFieldAttr(perLine ? pairedField : undefined)}
                onMouseEnter={perLine && pairedField ? markHover(pairedField) : undefined}
                onMouseLeave={perLine ? clearHoverMark : undefined}
              >
                {left.line2}
              </Box>
            </>
          ) : (
            left.line1
          )}
        </Box>
      )}
    </Box>
  );
};

export default AxisBoundaryHeader;
